/**
 * Documents Observable - Redux Observable Epics
 * Maneja los side effects del módulo de documentos
 */

import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs'

import DocumentsActions, { DocumentsTypes } from '../Redux/DocumentsRedux'
import { notificationSuccess, notificationFail } from '../Redux/NotificationActions'

/**
 * Epic para fetch de documentos del estudiante
 */
const fetchStudentDocumentsEpic = (action$, store, { Api }) =>
  action$
    .ofType(DocumentsTypes.fetchStudentDocumentsRequest)
    .mergeMap(({ studentId, filters }) =>
      Api.studentDocuments
        .get(studentId, filters)
        .then(response => DocumentsActions.fetchStudentDocumentsSuccess(
          response.data.data,
          response.data.meta
        ))
        .catch(error => DocumentsActions.fetchStudentDocumentsError(
          error.error || 'Error al cargar documentos'
        ))
    )

/**
 * Epic para búsqueda con debounce
 */
const searchStudentDocumentsEpic = (action$, store) =>
  action$
    .ofType(DocumentsTypes.searchStudentDocuments)
    .debounceTime(500)
    .map(({ studentId, searchTerm }) => {
      const filters = {
        ...store.getState().documents.studentRepository.filters,
        search: searchTerm,
      }
      return DocumentsActions.fetchStudentDocumentsRequest(studentId, filters)
    })

/**
 * Epic para eliminar documento del estudiante
 */
const deleteStudentDocumentEpic = (action$, store, { Api }) =>
  action$
    .ofType(DocumentsTypes.deleteStudentDocumentRequest)
    .mergeMap(({ studentId, documentId }) =>
      Observable.fromPromise(
        Api.studentDocuments
          .delete(studentId, documentId)
          .then(response => response.data)
          .catch(error => { throw error })
      ).flatMap(() => [
        DocumentsActions.deleteStudentDocumentSuccess(documentId),
        notificationSuccess('Documento eliminado correctamente')
      ]).catch(error => Observable.of(
        DocumentsActions.deleteStudentDocumentError(
          (error && error.error) || 'Error al eliminar documento'
        ),
        notificationFail((error && error.error) || 'Error al eliminar documento')
      ))
    )

/**
 * Epic para fetch de biblioteca de documentos
 */
const fetchDocumentsEpic = (action$, store, { Api }) =>
  action$
    .ofType(DocumentsTypes.fetchDocumentsRequest)
    .mergeMap(({ filters }) =>
      Api.documents
        .get(filters)
        .then(response => DocumentsActions.fetchDocumentsSuccess(
          response.data.data,
          response.data.meta
        ))
        .catch(error => DocumentsActions.fetchDocumentsError(
          error.error || 'Error al cargar biblioteca'
        ))
    )

/**
 * Epic para fetch de versiones de un documento
 */
const fetchDocumentVersionsEpic = (action$, store, { Api }) =>
  action$
    .ofType(DocumentsTypes.fetchDocumentVersionsRequest)
    .mergeMap(({ documentId }) =>
      Api.documents
        .getVersions(documentId)
        .then(response => DocumentsActions.fetchDocumentVersionsSuccess(
          documentId,
          response.data.data
        ))
        .catch(error => DocumentsActions.fetchDocumentVersionsError(
          error.error || 'Error al cargar versiones'
        ))
    )

/**
 * Epic para fetch de historial de distribución de un documento
 */
const fetchDistributionHistoryEpic = (action$, store, { Api }) =>
  action$
    .ofType(DocumentsTypes.fetchDistributionHistoryRequest)
    .mergeMap(({ documentId }) =>
      Api.documents
        .getDistributionHistory(documentId)
        .then(response => DocumentsActions.fetchDistributionHistorySuccess(
          documentId,
          response.data.data
        ))
        .catch(error => DocumentsActions.fetchDistributionHistoryError(
          error.error || 'Error al cargar historial'
        ))
    )

/**
 * Epic para descargar versión de un documento
 */
const downloadVersionEpic = (action$, store, { Api }) =>
  action$
    .ofType(DocumentsTypes.downloadVersionRequest)
    .mergeMap(({ documentId, versionId }) =>
      Observable.fromPromise(
        Api.documents
          .downloadVersion(documentId, versionId)
          .then(response => response)
          .catch(error => { throw error })
      ).flatMap(function (response) {
        var file = response.headers['content-disposition']
        var fileName = 'documento'
        if (file) {
          var index = file.indexOf('=')
          if (index > -1) {
            fileName = file.substring(index + 1, file.length).replace(/"/g, '').trim()
          }
        }
        var url = window.URL.createObjectURL(new Blob([response.data]))
        var link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        return [DocumentsActions.downloadVersionSuccess()]
      }).catch(error => Observable.of(
        DocumentsActions.downloadVersionError(
          (error && error.error) || 'Error al descargar'
        ),
        notificationFail('Error al descargar el documento')
      ))
    )

/**
 * Epic para descargar documento del repositorio del estudiante
 */
const downloadStudentDocumentEpic = (action$, store, { Api }) =>
  action$
    .ofType(DocumentsTypes.downloadStudentDocumentRequest)
    .mergeMap(({ studentId, documentId }) =>
      Observable.fromPromise(
        Api.studentDocuments
          .download(studentId, documentId)
          .then(response => response)
          .catch(error => { throw error })
      ).flatMap(function (response) {
        var file = response.headers['content-disposition']
        var fileName = 'documento'
        if (file) {
          var index = file.indexOf('=')
          if (index > -1) {
            fileName = file.substring(index + 1, file.length).replace(/"/g, '')
          }
        }
        var url = window.URL.createObjectURL(new Blob([response.data]))
        var link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        return [DocumentsActions.downloadStudentDocumentSuccess()]
      }).catch(error => Observable.of(
        DocumentsActions.downloadStudentDocumentError(
          (error && error.error) || 'Error al descargar'
        ),
        notificationFail('Error al descargar el documento')
      ))
    )

/**
 * Epic para check de duplicados
 * NOTA: Usa el mismo endpoint de distribute con dry_run=true
 */
const checkDuplicatesEpic = (action$, store, { Api }) =>
  action$
    .ofType(DocumentsTypes.checkDuplicatesRequest)
    .mergeMap(({ documentIds, studentIds }) =>
      Api.distribution
        .distribute(documentIds[0], { student_ids: studentIds, dry_run: true })
        .then(response => DocumentsActions.checkDuplicatesSuccess(
          response.data.data || []
        ))
        .catch(error => DocumentsActions.checkDuplicatesError(
          error.error || 'Error al verificar duplicados'
        ))
    )

/**
 * Epic para distribución de documentos
 */
const distributeDocumentsEpic = (action$, store, { Api }) =>
  action$
    .ofType(DocumentsTypes.distributeDocumentsRequest)
    .mergeMap(({ payload }) =>
      Observable.fromPromise(
        Api.distribution
          .distribute(payload.documentId, {
            student_ids: payload.studentIds,
            send_email: payload.sendEmail
          })
          .then(response => response.data)
          .catch(error => { throw error })
      ).flatMap((result) => {
        // Success total
        if (result.distributed_count === result.total_requested) {
          return [
            DocumentsActions.distributeDocumentsSuccess(result),
            notificationSuccess(
              result.distributed_count + ' documentos distribuidos correctamente'
            ),
            DocumentsActions.closeWizard()
          ]
        }

        // Success parcial (algunos omitidos por duplicados)
        if (result.distributed_count > 0 && result.omitted_count > 0) {
          return [
            DocumentsActions.distributeDocumentsSuccess(result),
            notificationSuccess(
              'Distribución completada: ' + result.distributed_count +
              ' distribuidos, ' + result.omitted_count + ' omitidos por duplicados'
            ),
            DocumentsActions.closeWizard()
          ]
        }

        // Ninguno distribuido
        return [
          DocumentsActions.distributeDocumentsSuccess(result),
          notificationFail(
            'No se distribuyó ningún documento. Todos ya existían en los repositorios.'
          )
        ]
      }).catch(error => Observable.of(
        DocumentsActions.distributeDocumentsError(
          (error && error.error) || 'Error al distribuir documentos'
        ),
        notificationFail((error && error.error) || 'Error al distribuir documentos')
      ))
    )

/**
 * Epic para abrir wizard con preselección de estudiante
 * Si viene preselectedStudentId, fetch student info usando el endpoint existente
 */
const openWizardWithStudentEpic = (action$, store, { Api }) =>
  action$
    .ofType(DocumentsTypes.openWizard)
    .filter(({ preselectedStudentId }) => !!preselectedStudentId)
    .mergeMap(({ preselectedStudentId }) =>
      Api.classrooms
        .getStudentProfile(preselectedStudentId)
        .then(response => DocumentsActions.selectStudent(response.data.data))
        .catch(() => Observable.empty())
    )

/**
 * Exportar observers
 */
export const documentsObservers = {
  fetchStudentDocumentsEpic,
  searchStudentDocumentsEpic,
  deleteStudentDocumentEpic,
  fetchDocumentsEpic,
  fetchDocumentVersionsEpic,
  fetchDistributionHistoryEpic,
  downloadVersionEpic,
  downloadStudentDocumentEpic,
  checkDuplicatesEpic,
  distributeDocumentsEpic,
  openWizardWithStudentEpic
}

/**
 * Combinar todos los epics
 */
export const documentsEpic = combineEpics(
  fetchStudentDocumentsEpic,
  searchStudentDocumentsEpic,
  deleteStudentDocumentEpic,
  fetchDocumentsEpic,
  fetchDocumentVersionsEpic,
  fetchDistributionHistoryEpic,
  downloadVersionEpic,
  downloadStudentDocumentEpic,
  checkDuplicatesEpic,
  distributeDocumentsEpic,
  openWizardWithStudentEpic
)



// WEBPACK FOOTER //
// ./src/Data/Observables/DocumentsObservable.js